export interface ConversionSegment {
  key: string;
  sessions: number;
  started: number;
  reachedPayment: number;
  booked: number;
  startRate: number;
  bookingRate: number;
}

export interface ConversionInsights {
  funnel: {
    key: string;
    label: string;
    count: number;
    pctOfLanded: number;
    pctOfPrevious: number;
    lost: number;
  }[];
  landed: number;
  booked: number;
  callClickSessions: number;
  overallBookingRate: number;
  bouncedAvgEngagementSec: number | null;
  bouncedAvgScrollDepth: number | null;
  startedAvgEngagementSec: number | null;
  byDevice: ConversionSegment[];
  bySource: ConversionSegment[];
  byLandingPage: ConversionSegment[];
  topValidationErrors: { key: string; count: number }[];
  topCardErrors: { key: string; count: number }[];
  topFareQuoteErrors: { key: string; count: number }[];
}

/** What a big drop at each funnel stage usually means. */
const DROP_HINTS: Record<string, string> = {
  form_started:
    "Visitors leave without touching the form — check page speed, form visibility above the fold, and whether ads/keywords match the page.",
  vehicle:
    "Address / airport search is not working for people — check the validation and fare-quote errors below.",
  when: "Visitors drop right after seeing prices — likely price shock or comparison shopping.",
  payment: "Form feels too long before payment — consider fewer fields or steps.",
  card_started:
    "Visitors reach payment but don't enter a card — trust issue or they want to pay cash / call instead.",
  card_completed: "Card entry problems — see card error codes below.",
  booked: "Card entered but booking failed — check payment failures / 3DS.",
};

const MIN_SEGMENT_SESSIONS = 10;

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-white/[0.03]">
      <h3 className="mb-3 text-base font-semibold text-gray-800 dark:text-white/90">{title}</h3>
      {children}
    </div>
  );
}

function SegmentTable({ title, rows }: { title: string; rows: ConversionSegment[] }) {
  const significant = rows.filter((r) => r.sessions >= MIN_SEGMENT_SESSIONS);
  const best = significant.length
    ? Math.max(...significant.map((r) => r.bookingRate))
    : 0;
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">No data.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase text-gray-500">
                <th className="py-1 pr-2">Segment</th>
                <th className="py-1 pr-2 text-right">Visits</th>
                <th className="py-1 pr-2 text-right">Started</th>
                <th className="py-1 pr-2 text-right">Booked</th>
                <th className="py-1 text-right">Book %</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const weak =
                  r.sessions >= MIN_SEGMENT_SESSIONS && best > 0 && r.bookingRate < best / 2;
                return (
                  <tr key={r.key} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="max-w-[220px] truncate py-1.5 pr-2 text-gray-700 dark:text-gray-300" title={r.key}>
                      {r.key}
                    </td>
                    <td className="py-1.5 pr-2 text-right">{r.sessions}</td>
                    <td className="py-1.5 pr-2 text-right">{r.startRate}%</td>
                    <td className="py-1.5 pr-2 text-right">{r.booked}</td>
                    <td className={`py-1.5 text-right font-medium ${weak ? "text-red-600" : "text-gray-800 dark:text-white/90"}`}>
                      {r.bookingRate}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-xs text-gray-500">
            Red = less than half the best segment's booking rate (min {MIN_SEGMENT_SESSIONS} visits).
          </p>
        </div>
      )}
    </Card>
  );
}

function CountList({ title, rows, empty }: { title: string; rows: { key: string; count: number }[]; empty: string }) {
  return (
    <Card title={title}>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500">{empty}</p>
      ) : (
        <ul className="space-y-1.5 text-sm">
          {rows.map((r) => (
            <li key={r.key} className="flex justify-between gap-3">
              <span className="text-gray-700 dark:text-gray-300">{r.key}</span>
              <span className="font-medium text-gray-800 dark:text-white/90">{r.count}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export default function ConversionInsightsPanel({
  insights,
  loading,
}: {
  insights: ConversionInsights | null;
  loading: boolean;
}) {
  if (loading && !insights) {
    return <p className="mb-6 text-sm text-gray-500">Loading conversion insights…</p>;
  }
  if (!insights || insights.landed === 0) {
    return <p className="mb-6 text-sm text-gray-500">No visitor data for this period.</p>;
  }

  const stages = insights.funnel.slice(1);
  const worst = stages.reduce((a, b) => (b.lost > a.lost ? b : a), stages[0]);

  return (
    <div className="mb-8 space-y-4">
      <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-200">
        <p className="font-semibold">
          {insights.landed} human visitors → {insights.booked} bookings ({insights.overallBookingRate}%)
          {insights.callClickSessions > 0 && `, plus ${insights.callClickSessions} tapped to call`}
        </p>
        {worst && worst.lost > 0 && (
          <p className="mt-1">
            Biggest loss: <strong>{worst.lost}</strong> visitors lost before “{worst.label}”.{" "}
            {DROP_HINTS[worst.key]}
          </p>
        )}
        {insights.bouncedAvgEngagementSec != null && (
          <p className="mt-1">
            Visitors who never started the form stayed {insights.bouncedAvgEngagementSec}s on average
            {insights.bouncedAvgScrollDepth != null && ` and scrolled ${insights.bouncedAvgScrollDepth}%`}
            {insights.bouncedAvgEngagementSec < 15
              ? " — very short: the page or traffic doesn't match what they searched for."
              : "."}
          </p>
        )}
      </div>

      <Card title="Landing → booking funnel">
        <div className="space-y-2">
          {insights.funnel.map((s) => (
            <div key={s.key}>
              <div className="flex justify-between text-sm">
                <span className="text-gray-700 dark:text-gray-300">{s.label}</span>
                <span className="text-gray-800 dark:text-white/90">
                  <strong>{s.count}</strong> · {s.pctOfLanded}%
                  {s.lost > 0 && (
                    <span className={`ml-2 text-xs ${s.key === worst?.key ? "text-red-600 font-semibold" : "text-gray-500"}`}>
                      −{s.lost} ({s.pctOfPrevious}% kept)
                    </span>
                  )}
                </span>
              </div>
              <div className="mt-1 h-2 rounded bg-gray-100 dark:bg-gray-800">
                <div
                  className={`h-2 rounded ${s.key === worst?.key ? "bg-red-500" : "bg-brand-500"}`}
                  style={{ width: `${Math.max(s.pctOfLanded, 0.5)}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <SegmentTable title="By device" rows={insights.byDevice} />
        <SegmentTable title="By traffic source" rows={insights.bySource} />
        <SegmentTable title="By landing page" rows={insights.byLandingPage} />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <CountList title="Top form validation errors" rows={insights.topValidationErrors} empty="No validation errors." />
        <CountList title="Fare quote failures" rows={insights.topFareQuoteErrors} empty="No fare quote failures." />
        <CountList title="Card error codes" rows={insights.topCardErrors} empty="No card errors." />
      </div>
    </div>
  );
}
